/** 컨테이너 배차일보 — 엑셀 양식 2행·병합 그리드 유틸 */

import { formatPhoneKorean } from './phone';
import { formatAmountDisplay } from './numberUtils';

export const GRID_COL_COUNT = 16;
export const ROWS_PER_RECORD = 2;
export const MIN_LOGICAL_RECORDS = 15;

export interface LogicalRecord {
  customer: string;
  shipperName: string;
  destination: string;
  shipperContact: string;
  itemName: string;
  shippingLine: string;
  licenseNo: string;
  containerSpec: string;
  weight: string;
  blOrSpecial: string;
  fullEmpty: string;
  loadTerminal: string;
  unloadTerminal: string;
  carrier: string;
  vehicleNumber: string;
  driverPhone: string;
  containerNo: string;
  sealNo: string;
  billingAmount: string;
  paymentAmount: string;
  settlementRate: string;
  remarks: string;
}

export interface ContainerRowApi {
  rowId: string;
  version: number;
  sortOrder?: number;
  customer?: string;
  shipperName?: string;
  destination?: string;
  shipperContact?: string;
  itemName?: string;
  shippingLine?: string;
  licenseNo?: string;
  containerSpec?: string;
  weight?: string | number;
  blOrSpecial?: string;
  fullEmpty?: string;
  loadTerminal?: string;
  unloadTerminal?: string;
  carrier?: string;
  vehicleNumber?: string;
  driverPhone?: string;
  containerNo?: string;
  sealNo?: string;
  billingAmount?: string | number;
  paymentAmount?: string | number;
  settlementRate?: string | number;
  remarks?: string;
  shipperMemo?: string;
}

export interface MergeCellConfig {
  row: number;
  col: number;
  rowspan: number;
  colspan: number;
}

function emptyGridRow(): string[] {
  return Array(GRID_COL_COUNT).fill('');
}

function splitFullEmpty(val: string): [string, string] {
  const v = String(val || '').toUpperCase().trim();
  return [v.includes('F') ? 'F' : '', v.includes('E') ? 'E' : ''];
}

function joinFullEmpty(f: string, e: string): string {
  const fs = String(f || '').trim().toUpperCase();
  const es = String(e || '').trim().toUpperCase();
  if (fs && es) return 'F/E';
  if (fs) return 'F';
  if (es) return 'E';
  return '';
}

function str(v: unknown): string {
  if (v === undefined || v === null) return '';
  return String(v);
}

export function emptyLogicalRecord(): LogicalRecord {
  return {
    customer: '',
    shipperName: '',
    destination: '',
    shipperContact: '',
    itemName: '',
    shippingLine: '',
    licenseNo: '',
    containerSpec: '',
    weight: '',
    blOrSpecial: '',
    fullEmpty: '',
    loadTerminal: '',
    unloadTerminal: '',
    carrier: '',
    vehicleNumber: '',
    driverPhone: '',
    containerNo: '',
    sealNo: '',
    billingAmount: '',
    paymentAmount: '',
    settlementRate: '',
    remarks: '',
  };
}

export function apiRowToLogical(r: ContainerRowApi): LogicalRecord {
  return {
    customer: r.customer || '',
    shipperName: r.shipperName || '',
    destination: r.destination || '',
    shipperContact: r.shipperContact || '',
    itemName: r.itemName || '',
    shippingLine: r.shippingLine || '',
    licenseNo: r.licenseNo || '',
    containerSpec: r.containerSpec || '',
    weight: str(r.weight),
    blOrSpecial: r.blOrSpecial || '',
    fullEmpty: r.fullEmpty || '',
    loadTerminal: r.loadTerminal || '',
    unloadTerminal: r.unloadTerminal || '',
    carrier: r.carrier || '',
    vehicleNumber: r.vehicleNumber || '',
    driverPhone: r.driverPhone || '',
    containerNo: r.containerNo || '',
    sealNo: r.sealNo || '',
    billingAmount: str(r.billingAmount),
    paymentAmount: str(r.paymentAmount),
    settlementRate: str(r.settlementRate),
    remarks: r.remarks || '',
  };
}

export interface ContainerRowMeta {
  rowId: string | null;
  version: number;
  shipperMemo: string;
}

function emptyContainerRowMeta(): ContainerRowMeta {
  return { rowId: null, version: 1, shipperMemo: '' };
}

/** API 행을 sort_order 위치에 배치 (빈 칸 유지) */
export function apiRowsToLogicalLayout(apiRows: ContainerRowApi[]): {
  records: LogicalRecord[];
  meta: ContainerRowMeta[];
} {
  if (apiRows.length === 0) {
    const records = padLogicalRecords([]);
    return {
      records,
      meta: records.map(() => emptyContainerRowMeta()),
    };
  }

  const hasSortOrder = apiRows.some(
    row => row.sortOrder != null && Number.isFinite(Number(row.sortOrder)) && Number(row.sortOrder) >= 0,
  );

  let maxIndex = -1;
  if (hasSortOrder) {
    for (const row of apiRows) {
      if (row.sortOrder == null || !Number.isFinite(Number(row.sortOrder))) continue;
      maxIndex = Math.max(maxIndex, Number(row.sortOrder));
    }
  }

  let length = hasSortOrder
    ? Math.max(MIN_LOGICAL_RECORDS, maxIndex + 1)
    : Math.max(MIN_LOGICAL_RECORDS, apiRows.length);

  const records: LogicalRecord[] = Array.from({ length }, () => emptyLogicalRecord());
  const meta: ContainerRowMeta[] = Array.from({ length }, () => emptyContainerRowMeta());

  apiRows.forEach((apiRow, i) => {
    const sortIdx = apiRow.sortOrder != null && Number.isFinite(Number(apiRow.sortOrder))
      ? Number(apiRow.sortOrder)
      : null;
    const idx = hasSortOrder
      ? (sortIdx != null && sortIdx >= 0 ? sortIdx : i)
      : i;

    while (idx >= records.length) {
      records.push(emptyLogicalRecord());
      meta.push(emptyContainerRowMeta());
    }

    records[idx] = apiRowToLogical(apiRow);
    meta[idx] = {
      rowId: apiRow.rowId || null,
      version: apiRow.version || 1,
      shipperMemo: apiRow.shipperMemo || '',
    };
  });

  const padded = padLogicalRecords(records);
  while (meta.length < padded.length) {
    meta.push(emptyContainerRowMeta());
  }

  return { records: padded, meta };
}

export function logicalToVisualPair(rec: LogicalRecord): [string[], string[]] {
  const [fVal, eVal] = splitFullEmpty(rec.fullEmpty);
  const top = emptyGridRow();
  const bottom = emptyGridRow();

  top[0] = rec.customer;
  top[1] = rec.shipperName;
  top[3] = rec.itemName;
  top[4] = rec.shippingLine;
  top[5] = rec.containerSpec;
  top[6] = rec.weight;
  top[7] = fVal;
  top[8] = eVal;
  top[9] = rec.loadTerminal;
  top[10] = rec.carrier;
  top[11] = rec.vehicleNumber;
  top[12] = rec.containerNo;
  top[13] = rec.billingAmount ? formatAmountDisplay(rec.billingAmount) : '';
  top[14] = rec.settlementRate ? formatAmountDisplay(rec.settlementRate) : '';
  top[15] = rec.remarks;

  bottom[1] = rec.destination;
  bottom[2] = rec.shipperContact ? formatPhoneKorean(rec.shipperContact) : '';
  bottom[4] = rec.licenseNo;
  bottom[6] = rec.blOrSpecial;
  bottom[9] = rec.unloadTerminal;
  bottom[11] = rec.driverPhone ? formatPhoneKorean(rec.driverPhone) : '';
  bottom[12] = rec.sealNo;
  bottom[13] = rec.paymentAmount ? formatAmountDisplay(rec.paymentAmount) : '';

  return [top, bottom];
}

export function visualPairToLogical(
  top: string[],
  bottom: string[],
  visualRowStart = 0
): LogicalRecord {
  const cleanAmount = (val: unknown) => {
    const s = String(val ?? '').trim();
    if (!s) return '';
    const num = Number(s.replace(/[^\d.-]/g, ''));
    if (isNaN(num)) return s.replace(/,/g, '');
    return String(num);
  };
  const clean = (visualRow: number, col: number, val: unknown) => {
    const s = String(val ?? '').trim();
    if (!s) return '';
    const hint = getCellPlaceholder(visualRow, col);
    if (hint && s === hint) return '';
    return s;
  };
  const r = visualRowStart;
  return {
    customer: clean(r, 0, top[0]),
    shipperName: clean(r, 1, top[1]),
    destination: clean(r + 1, 1, bottom[1]),
    shipperContact: clean(r + 1, 2, bottom[2]),
    itemName: clean(r, 3, top[3]),
    shippingLine: clean(r, 4, top[4]),
    licenseNo: clean(r + 1, 4, bottom[4]),
    containerSpec: clean(r, 5, top[5]),
    weight: clean(r, 6, top[6]),
    blOrSpecial: clean(r + 1, 6, bottom[6]),
    fullEmpty: joinFullEmpty(clean(r, 7, top[7]), clean(r, 8, top[8])),
    loadTerminal: clean(r, 9, top[9]),
    unloadTerminal: clean(r + 1, 9, bottom[9]),
    carrier: clean(r, 10, top[10]),
    vehicleNumber: clean(r, 11, top[11]),
    driverPhone: clean(r + 1, 11, bottom[11]),
    containerNo: clean(r, 12, top[12]),
    sealNo: clean(r + 1, 12, bottom[12]),
    billingAmount: cleanAmount(top[13]),
    paymentAmount: cleanAmount(bottom[13]),
    settlementRate: cleanAmount(top[14]),
    remarks: clean(r, 15, top[15]),
  };
}

export function logicalRecordsToGrid(records: LogicalRecord[]): string[][] {
  const grid: string[][] = [];
  for (const rec of records) {
    const [top, bottom] = logicalToVisualPair(rec);
    grid.push(top, bottom);
  }
  return grid;
}

export function gridToLogicalRecords(grid: string[][]): LogicalRecord[] {
  const records: LogicalRecord[] = [];
  for (let i = 0; i < grid.length; i += ROWS_PER_RECORD) {
    const top = grid[i] || emptyGridRow();
    const bottom = grid[i + 1] || emptyGridRow();
    records.push(visualPairToLogical(top, bottom, i));
  }
  return records;
}

export function isLogicalRecordEmpty(rec: LogicalRecord): boolean {
  return Object.values(rec).every(v => !String(v || '').trim());
}

export function logicalIndexFromVisualRow(visualRow: number): number {
  return Math.floor(visualRow / ROWS_PER_RECORD);
}

export function buildMergeCellsForRecord(visualRowStart: number): MergeCellConfig[] {
  const r = visualRowStart;
  return [
    { row: r, col: 0, rowspan: 2, colspan: 1 },
    { row: r, col: 1, rowspan: 1, colspan: 2 },
    { row: r, col: 3, rowspan: 2, colspan: 1 },
    { row: r + 1, col: 4, rowspan: 1, colspan: 2 },
    { row: r + 1, col: 6, rowspan: 1, colspan: 3 },
    { row: r, col: 10, rowspan: 2, colspan: 1 },
    { row: r, col: 14, rowspan: 2, colspan: 1 },
    { row: r, col: 15, rowspan: 2, colspan: 1 },
  ];
}

export function buildAllMergeCells(visualRowCount: number): MergeCellConfig[] {
  const merges: MergeCellConfig[] = [];
  for (let r = 0; r < visualRowCount; r += ROWS_PER_RECORD) {
    merges.push(...buildMergeCellsForRecord(r));
  }
  return merges;
}

export function padLogicalRecords(records: LogicalRecord[], minCount = MIN_LOGICAL_RECORDS): LogicalRecord[] {
  const next = [...records];
  while (next.length < minCount) {
    next.push(emptyLogicalRecord());
  }
  return next;
}

export function buildRowPayload(
  rec: LogicalRecord,
  logicalIndex: number,
  meta: { rowId: string | null; version: number; shipperMemo?: string }
) {
  return {
    rowId: meta.rowId || undefined,
    version: meta.version,
    sortOrder: logicalIndex,
    customer: rec.customer,
    shipperName: rec.shipperName,
    destination: rec.destination,
    shipperContact: rec.shipperContact,
    itemName: rec.itemName,
    shippingLine: rec.shippingLine,
    licenseNo: rec.licenseNo,
    containerSpec: rec.containerSpec,
    weight: rec.weight,
    blOrSpecial: rec.blOrSpecial,
    fullEmpty: rec.fullEmpty,
    loadTerminal: rec.loadTerminal,
    unloadTerminal: rec.unloadTerminal,
    carrier: rec.carrier,
    vehicleNumber: rec.vehicleNumber,
    driverPhone: rec.driverPhone,
    containerNo: rec.containerNo,
    sealNo: rec.sealNo,
    billingAmount: rec.billingAmount,
    paymentAmount: rec.paymentAmount,
    settlementRate: rec.settlementRate,
    remarks: rec.remarks,
    shipperMemo: meta.shipperMemo ?? '',
  };
}

export const CUSTOM_HEADER_HEIGHT = 56;

/** 행 번호 열 너비 (Handsontable rowHeaders) */
export const CD_ROW_HEADER_WIDTH = 50;

/** 엑셀 양식 기준 기본 열 너비 비율 */
export const CD_DEFAULT_COL_WIDTHS: number[] = [
  88, 72, 72, 64, 44, 44, 52, 28, 28, 72, 72, 72, 88, 64, 56, 72,
];

export function scaleColumnWidthsToFit(
  containerWidth: number,
  rowHeaderWidth = CD_ROW_HEADER_WIDTH,
  baseWidths: number[] = CD_DEFAULT_COL_WIDTHS
): number[] {
  const available = Math.max(320, containerWidth - rowHeaderWidth);
  const baseSum = baseWidths.reduce((sum, w) => sum + w, 0);
  const scale = available / baseSum;
  const scaled = baseWidths.map(w => Math.max(24, Math.round(w * scale)));
  const diff = available - scaled.reduce((sum, w) => sum + w, 0);
  if (diff !== 0 && scaled.length > 0) {
    scaled[scaled.length - 1] = Math.max(24, scaled[scaled.length - 1] + diff);
  }
  return scaled;
}

export function tablePixelWidth(
  colWidths: number[],
  rowHeaderWidth = CD_ROW_HEADER_WIDTH
): number {
  return rowHeaderWidth + colWidths.reduce((sum, w) => sum + w, 0);
}

export const NESTED_HEADERS = [
  [
    '거래처',
    { label: '도착 창고및업체', colspan: 2 },
    '품명',
    'LINE',
    '규격',
    '무게',
    'F',
    'E',
    '반출터미널',
    '운송업체',
    '차량번호',
    '컨테이너번호',
    '청구액',
    '정상요율',
    '비고',
  ],
  [
    '',
    '도착지역',
    '연락처',
    '',
    { label: '면허', colspan: 2 },
    { label: 'B/L no', colspan: 3 },
    '반납터미널',
    '',
    '연락처',
    '품명 및 비고',
    '하불액',
    '',
    '',
  ],
];

export const NUMERIC_GRID_COLS = new Set([6, 13, 14]);

/** 빈 셀 안내 문구 — 열 제목(nestedHeaders)이 표시되므로 최소화 */
export function getCellPlaceholder(visualRow: number, col: number): string {
  return '';
}

export function calcLogicalRecordsForHeight(
  gridHeightPx: number,
  rowHeight = 32,
  headerHeight = CUSTOM_HEADER_HEIGHT
): number {
  const dataHeight = Math.max(0, gridHeightPx - headerHeight);
  const visualRows = Math.floor(dataHeight / rowHeight);
  const logical = Math.floor(visualRows / ROWS_PER_RECORD);
  return Math.max(MIN_LOGICAL_RECORDS, logical);
}
